import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import cookie from "react-cookies";
import { COOKIE_SETUP_LIFECYCLE, RIMS_COOKIE_STG_CONSTANTS } from "../constants";
import { getCookieConfig, logoutFunction, redirectToLogin, removeAllCookiesAndLoginDomainInRootDomain } from "../common/utils";
import { clearInterval, setInterval } from 'worker-timers';
import axios from "axios";
import { basicUrl } from "../api";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';

export interface IProps {
  children?: JSX.Element[] | JSX.Element;
}

interface ICountDownDialogProps extends IProps {
  openDialog: boolean;
  handleStillHere: () => void;
  handleLogout: () => void;
  handleCloseDialog: () => void;
  sessionTimeoutCountdown?: number | null;
}

const CountDown = (props: ICountDownDialogProps): JSX.Element => {
  const { openDialog, handleLogout, handleStillHere, handleCloseDialog, sessionTimeoutCountdown } = props;
  
  // Get initial time from props or fallback to cookie
  const getInitialTime = useCallback(() => {
    if (sessionTimeoutCountdown) {
      return sessionTimeoutCountdown;
    }
    return parseInt(cookie.load(RIMS_COOKIE_STG_CONSTANTS.REMIAN_TIME), 10) || 60;
  }, [sessionTimeoutCountdown]);
  
  const [timeLeft, setTimeLeft] = useState(getInitialTime());
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  // Update timeLeft when sessionTimeoutCountdown prop changes
  useEffect(() => {
    if (sessionTimeoutCountdown) {
      setTimeLeft(sessionTimeoutCountdown);
    }
  }, [sessionTimeoutCountdown]);

  // Helper function to format time as MM:SS
  const formatTimeDisplay = (seconds: number): JSX.Element => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    
    if (minutes > 0 && remainingSeconds > 0) {
      return (
        <>
          <span
            style={{
              fontSize: "25px",
              fontWeight: "bold",
              color: "#000000",
            }}
          >
            {minutes}
          </span>
          <span>&nbsp;{t("systemSettings.sessionTimeOut.min")}&nbsp;</span>
          <span
            style={{
              fontSize: "25px",
              fontWeight: "bold",
              color: "#000000",
            }}
          >
            {remainingSeconds}
          </span>
          <span>&nbsp;{t("systemSettings.sessionTimeOut.sec")}</span>
        </>
      );
    } else if (minutes > 0) {
      return (
        <>
          <span
            style={{
              fontSize: "25px",
              fontWeight: "bold",
              color: "#000000",
            }}
          >
            {minutes}
          </span>
          <span>&nbsp;{t("systemSettings.sessionTimeOut.min")}</span>
        </>
      );
    } else {
      return (
        <>
          <span
            style={{
              fontSize: "25px",
              fontWeight: "bold",
              color: "#000000",
            }}
          >
            {remainingSeconds}
          </span>
          <span>&nbsp;{t("systemSettings.sessionTimeOut.sec")}</span>
        </>
      );
    }
  };

  useEffect(() => {
    let intervalRef: any = null;
    if (openDialog) {
      intervalRef = setInterval(() => {
        const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
        const refreshingSession = cookie.load(RIMS_COOKIE_STG_CONSTANTS.SESSION_LOCK);
        if (!token) {
          if (intervalRef) {
            clearInterval(intervalRef);
          }
          if (refreshingSession) {
            handleCloseDialog();
          } else {
            enqueueSnackbar(t("systemSettings.sessionTimeOut.logout"), {
              variant: "error",
              anchorOrigin: { vertical: "top", horizontal: "center" },
              preventDuplicate: true
            });
            setTimeout(() => {
              logoutFunction();
              redirectToLogin();
            }, 2000)
          }
        } else {
          if (refreshingSession) {
            if (intervalRef) {
              clearInterval(intervalRef);
            }
            handleCloseDialog();
          }
        }

        setTimeLeft((prevTime) => {
          if (prevTime < 1) {
            if (intervalRef) {
              clearInterval(intervalRef);
            }

            if (token) {
              if (!refreshingSession) {
              const refreshToken = cookie.load(RIMS_COOKIE_STG_CONSTANTS.REFRESH_TOKEN);
              axios({
                method: "delete",
                baseURL: basicUrl,
                url: '/common-auth/logout?session-timeout=true',
                headers: {
                  "Content-Type": "application/json;charset=UTF-8",
                  Authorization: `Bearer ${token}`,
                  'refresh-token': refreshToken
                },
              })
                .then()
                .catch()
                .finally(() => {
                  enqueueSnackbar(t("systemSettings.sessionTimeOut.logout"), {
                    variant: "error",
                    anchorOrigin: { vertical: "top", horizontal: "center" },
                    preventDuplicate: true
                  });
                  setTimeout(() => {
                    removeAllCookiesAndLoginDomainInRootDomain();
                    redirectToLogin();
                  }, 2000);

                });
              }
            }
            return 0;
          }
          const newTime = prevTime - 1;
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.REMIAN_TIME, newTime, getCookieConfig(newTime));
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (intervalRef) {
        clearInterval(intervalRef);
      }
      // Clean up cookie and reset state
      cookie.remove(RIMS_COOKIE_STG_CONSTANTS.REMIAN_TIME, COOKIE_SETUP_LIFECYCLE);
      setTimeLeft(getInitialTime());
    }
  }, [openDialog, enqueueSnackbar, getInitialTime, handleCloseDialog, t]);

  return (
    <Dialog className="dialog-customization" maxWidth="md" open={openDialog}>
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: 'none !important' }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {t("systemSettings.sessionTimeOut.title")}
        </Typography>
        <IconButton onClick={handleStillHere}>
          <CloseIcon style={{ fontSize: '24px' }} />
        </IconButton>
      </DialogTitle>
      <DialogContent className="form-group">
        <Grid container spacing={2} style={{ marginBottom: '-11px' }}>
          <Grid item xs={12} style={{ marginBottom: '-11px' }}>
            <Typography
              variant="body1"
              sx={{ my: 2, textAlign: "center" }}
            >
              <ErrorOutlineIcon style={{ fontSize: "48px", color: "#8c8c8c" }} />
            </Typography>

            <Typography
              variant="body1"
              sx={{ my: 2, textAlign: "center", color: "#707070", margin: '15px 0px 17px 17px' }}
            >
              {t("systemSettings.sessionTimeOut.prompt")}
            </Typography>
            <Typography variant="body1" sx={{ my: 2, textAlign: "center" }}>
              {formatTimeDisplay(timeLeft)}
            </Typography>
          </Grid>
        </Grid>
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
          <Button variant="text" size="small" onClick={handleLogout}>
            No
          </Button>
          <Button variant="contained" size="small" onClick={handleStillHere}>
            Yes
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default CountDown;
